import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './utils/api';

function SettingsDispatchCategories() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    icon: '📋',
    color: 'blue',
    sort_order: 0,
    mapping_field: 'job_category',
    mapping_values: ''
  });

  const colorOptions = [
    { name: 'Red', value: 'red', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-900' },
    { name: 'Orange', value: 'orange', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-900' },
    { name: 'Yellow', value: 'yellow', bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-900' },
    { name: 'Green', value: 'green', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-900' },
    { name: 'Teal', value: 'teal', bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-900' },
    { name: 'Blue', value: 'blue', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-900' },
    { name: 'Indigo', value: 'indigo', bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-900' },
    { name: 'Purple', value: 'purple', bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-900' },
    { name: 'Pink', value: 'pink', bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-900' },
    { name: 'Grey', value: 'gray', bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-900' }
  ];

  const iconOptions = ['🚨', '📞', '💼', '📋', '🔧', '📅', '⚙️', '🔍', '📦', '✅', '⏸️', '❌'];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await apiFetch('/api/dispatch-categories');
      const data = await res.json();
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleSave = async () => {
    try {
      const url = editingId
        ? `/api/dispatch-categories/${editingId}`
        : '/api/dispatch-categories';

      const res = await apiFetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        fetchCategories();
        resetForm();
      }
    } catch (err) {
      console.error('Error saving category:', err);
    }
  };

  const handleEdit = (cat) => {
    setEditingId(cat.id);
    setFormData({
      name: cat.name,
      icon: cat.icon,
      color: cat.color,
      sort_order: cat.sort_order,
      mapping_field: cat.mapping_field || 'job_category',
      mapping_values: cat.mapping_values || ''
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;

    try {
      await apiFetch(`/api/dispatch-categories/${id}`, {
        method: 'DELETE'
      });
      fetchCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      name: '',
      icon: '📋',
      color: 'blue',
      sort_order: categories.length,
      mapping_field: 'job_category',
      mapping_values: ''
    });
  };

  const getColorClasses = (color) => {
    const opt = colorOptions.find(c => c.value === color);
    return opt || colorOptions[5]; // default to blue
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/settings/dispatch')}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
              title="Back to Settings"
            >
              ⬅ Back
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold">Dispatch Categories</h1>
              <span className="text-sm text-blue-100">Configure job categories for dispatch board</span>
            </div>
            <div style={{ width: '100px' }}></div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {editingId ? 'Edit Category' : 'Add Category'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Emergency Callouts"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Icon
                </label>
                <div className="flex flex-wrap gap-2">
                  {iconOptions.map(icon => (
                    <button
                      key={icon}
                      onClick={() => setFormData({ ...formData, icon })}
                      className={`text-2xl p-2 rounded border-2 transition ${
                        formData.icon === icon
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {colorOptions.map(color => (
                    <button
                      key={color.value}
                      onClick={() => setFormData({ ...formData, color: color.value })}
                      className={`p-3 rounded border-2 transition ${color.bg} ${
                        formData.color === color.value
                          ? 'border-gray-900'
                          : color.border
                      }`}
                      title={color.name}
                    >
                      <div className={`text-xs font-medium ${color.text}`}>{color.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mapping Field
                </label>
                <select
                  value={formData.mapping_field}
                  onChange={(e) => setFormData({ ...formData, mapping_field: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="job_category">Job Category</option>
                  <option value="job_status">Job Status</option>
                  <option value="call_priority">Call Priority</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mapping Values (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.mapping_values}
                  onChange={(e) => setFormData({ ...formData, mapping_values: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., repair,emergency"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Jobs matching these values will appear in this category
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  onClick={handleSave}
                  disabled={!formData.name}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {editingId ? 'Update' : 'Add'} Category
                </button>
                {editingId && (
                  <button
                    onClick={resetForm}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* List */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Categories ({categories.length})
            </h2>

            <div className="space-y-2">
              {categories.sort((a, b) => a.sort_order - b.sort_order).map(cat => {
                const colors = getColorClasses(cat.color);
                return (
                  <div
                    key={cat.id}
                    className={`p-4 rounded-lg border-2 ${colors.bg} ${colors.border}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{cat.icon}</span>
                        <div>
                          <div className={`font-semibold ${colors.text}`}>{cat.name}</div>
                          <div className="text-xs text-gray-600 mt-1">
                            {cat.mapping_field}: {cat.mapping_values || 'none'}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(cat)}
                          className="px-3 py-1 bg-white border border-gray-300 rounded text-sm font-medium hover:bg-gray-50 transition"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(cat.id)}
                          className="px-3 py-1 bg-red-50 border border-red-200 text-red-700 rounded text-sm font-medium hover:bg-red-100 transition"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {categories.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No categories yet. Add one to get started!
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsDispatchCategories;
